import type { CSSProperties, MouseEvent } from 'react';

import { cx } from './classNames';

/** Pendant zu `SfCheckBox`. Ohne `onChange` ist die Box schreibgeschützt. */
export interface CheckBoxProps {
  checked: boolean | null;
  onChange?: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

export function CheckBox(props: CheckBoxProps) {
  const { onChange } = props;
  const checked = props.checked === true;
  // `null` steht im Altclient für „kein Wert" und wird wie bei Syncfusion als
  // unbestimmter Zustand (`e-stop`) dargestellt.
  const indeterminate = props.checked === null;
  const interactive = onChange !== undefined && props.disabled !== true;

  const toggle = (event: MouseEvent<HTMLElement>) => {
    if (!interactive) return;
    // Der Klick auf das versteckte Eingabefeld löst bereits `onChange` aus.
    if (event.target instanceof HTMLInputElement) return;
    onChange(!checked);
  };

  return (
    <span
      id={props.id}
      title={props.title}
      style={props.style}
      className={cx(
        'e-checkbox-wrapper',
        props.disabled === true && 'e-checkbox-disabled',
        props.className,
      )}
      onClick={toggle}
    >
      <input
        type="checkbox"
        className="e-control e-checkbox"
        checked={checked}
        disabled={props.disabled === true}
        readOnly={!interactive}
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-label={props.label}
        onChange={
          interactive
            ? (event) => {
                onChange(event.target.checked);
              }
            : undefined
        }
      />
      <span className={cx('e-frame', checked && 'e-check', indeterminate && 'e-stop')}>
        {checked ? <i className="fa-solid fa-check" /> : null}
        {indeterminate ? <i className="fa-solid fa-minus" /> : null}
      </span>
      {props.label === undefined ? null : <span className="e-label">{props.label}</span>}
    </span>
  );
}

export default CheckBox;
